import { useEffect, useMemo, useRef, useState } from 'react';
import { losApi } from '../losApi';
import { api, isSpendApprovalError } from '../api';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from '../errors';

// All page state + the request logic (manual calc dispatch, multi-file
// auto-analyze, history). Extracted verbatim from IncomeCalculatorPage.
export function useIncomeCalculator() {
  const [type, setType] = useState('w2_wage');
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [detailCalcId, setDetailCalcId] = useState(null); // for the History detail drawer

  // Agency / loan-type selector (drives which self-employed calculation rules apply)
  const [loanType, setLoanType] = useState(() => {
    // Inherit from ?loan_type=... URL param if present (e.g., LoanFile → deep-link)
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search).get('loan_type');
      if (p) return p.toLowerCase();
    }
    return 'conventional';
  });
  const [agencyRules, setAgencyRules] = useState([]);

  // Opened from a loan file's Financial Info (?loan=<id>&borrower=<name>): the borrower
  // is preselected and a finished run can be saved back to that loan.
  const [loanCtx] = useState(() => {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search);
    const id = q.get('loan');
    return id ? { loanId: id, borrower: q.get('borrower') || '' } : null;
  });
  const [savedToLoan, setSavedToLoan] = useState(null); // calc_ids already saved
  const [savingToLoan, setSavingToLoan] = useState(false);

  // Multi-file AI state
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [requestingDocs, setRequestingDocs] = useState(false);
  const [sendToBorrower, setSendToBorrower] = useState(false);
  const fileInputRef = useRef(null);

  // An analysis that switches the tile leaves its figures here, so the
  // type-change reset below hands them over instead of wiping them.
  const pendingFill = useRef(null);
  const typeRef = useRef(type);
  typeRef.current = type;

  useEffect(() => {
    const fill = pendingFill.current;
    pendingFill.current = null;
    setForm(fill ? fill.form : {});
    setResult(fill ? fill.result : null);
  }, [type]);
  const loadHist = async () => { const { data } = await api.get('/income/history'); setHistory(data); };
  useEffect(() => { loadHist(); }, []);

  // Fetch agency rules for the dropdown once on mount
  useEffect(() => {
    api.get('/income/agency-rules').then(({ data }) => {
      setAgencyRules(data.loan_types || []);
    }).catch(() => {});
  }, []);

  const currentRules = useMemo(
    () => agencyRules.find((r) => r.key === loanType) || null,
    [agencyRules, loanType],
  );

  const withBorrower = (f) => (loanCtx?.borrower && !f.borrower_name ? { ...f, borrower_name: loanCtx.borrower } : f);

  const setNum = (k) => (e) => setForm({ ...form, [k]: e.target.value === '' ? undefined : Number(e.target.value) });

  const calc = async () => {
    setBusy(true); setResult(null);
    try {
      // For merged W-2 wage earner: dispatch to correct backend type + auto-add OT/bonus.
      if (type === 'w2_wage') {
        // A rate alone is hourly; blank hours default to 40 with an assumption note.
        const isHourly = !!form.hourly_rate;
        const primaryType = isHourly ? 'w2_hourly' : 'w2_salary';
        const primary = await api.post('/income/calculate', { ...withBorrower(form), income_type: primaryType });
        let combined = primary.data;

        // If overtime/bonus figures present, run a second pass and sum
        // A refused base skips the second call, so no stray OT row is saved.
        const hasOT = (form.overtime_y1 || 0) + (form.overtime_y2 || 0) > 0;
        if (hasOT && primary.data.qualifying_monthly != null) {
          const otCalc = await api.post('/income/calculate', {
            income_type: 'w2_overtime_bonus',
            overtime_y1: form.overtime_y1,
            overtime_y2: form.overtime_y2,
            notes: form.notes,
            ...(loanCtx?.borrower ? { borrower_name: loanCtx.borrower } : {}),
          });
          // A refused part (missing field) refuses the total — never count it as $0.
          const p = primary.data, o = otCalc.data;
          const refused = p.qualifying_monthly == null || o.qualifying_monthly == null;
          combined = {
            ...p,
            calc_ids: [p.calc_id, o.calc_id], // saved to a loan as one summed figure
            qualifying_monthly: refused ? null : p.qualifying_monthly + o.qualifying_monthly,
            missing_fields: [...(p.missing_fields || []), ...(o.missing_fields || [])],
            assumptions: [...(p.assumptions || []), ...(o.assumptions || [])],
            method: `${primary.data.method}; + variable: ${otCalc.data.method}`,
            breakdown: [
              ...(primary.data.breakdown || []),
              { label: '─── Variable Pay (24-mo avg) ───', value: '' },
              ...(otCalc.data.breakdown || []),
            ],
          };
        }
        setResult(combined);
      } else {
        const { data } = await api.post('/income/calculate', { income_type: type, ...withBorrower(form) });
        setResult(data);
      }
      loadHist();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Calculation error' }); }
    setBusy(false);
  };

  const saveToLoan = async () => {
    if (!loanCtx || !result || result.qualifying_monthly == null) return;
    const ids = result.calc_ids || [result.calc_id];
    setSavingToLoan(true);
    try {
      const r = await losApi.saveIncomeAnalysis(loanCtx.loanId, ids);
      if (r.applied) { setSavedToLoan(ids); toast({ title: 'Saved to the loan file', description: 'Financial Info now shows this figure.' }); }
      else toast({ title: 'Not saved', description: 'The loan already has a newer analysis.' });
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e) }); }
    setSavingToLoan(false);
  };

  const requestVariableIncomeDocs = async () => {
    setRequestingDocs(true);
    try {
      const vi = analysis?.variable_income;
      await api.post('/income/request-additional-docs', {
        borrower_name: analysis?.borrower_name || 'borrower',
        doc_types_needed: vi?.docs_needed || [],
        reason: vi?.checklist_message || 'Variable income detected — 24-month history needed.',
      });
      toast({ title: 'Task created', description: 'Added to Tasks queue with priority: High' });
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e) }); }
    setRequestingDocs(false);
  };

  const del = async (id) => {
    if (!(await confirmDialog({ title: 'Delete this calculation?', confirmText: 'Delete', destructive: true }))) return;
    try { await api.delete(`/income/${id}`); loadHist(); }
    catch { toast({ title: 'Delete failed' }); }
  };

  // ---- MULTI-FILE AUTO ANALYZE ----
  // Ticket 176: the last drop is kept so a file held for an SSN mask review
  // can be re-read right after the review, without dropping it again.
  const lastFiles = useRef([]);
  const running = useRef(false); // one read at a time
  // Files of the drop on screen that were reviewed and wait for the drop's other
  // held files, so the drop is read again once, not once per review.
  const [dropReviewed, setDropReviewed] = useState(() => new Set());
  // Held drops whose copy the server still has (waiting for a review, or
  // reviewed and not read yet). Loaded from the server, so a refresh keeps them.
  const [waiting, setWaiting] = useState([]);
  const loadWaiting = async () => {
    try {
      const { data } = await api.get('/income/ssn-review');
      const items = data?.items || [];
      setWaiting(items);
      return items;
    } catch { return []; } // the list is a convenience; the page works without it
  };
  useEffect(() => { loadWaiting(); }, []);

  const analyzeDocs = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    await runAnalysis(fd, files);
  };

  // Reviewed drops read from the server's stored copy: no second drop needed.
  const readReviewed = async (reviewIds) => {
    const ids = (reviewIds || []).filter(Boolean);
    if (ids.length === 0) return;
    const fd = new FormData();
    ids.forEach((id) => fd.append('review_ids', id));
    await runAnalysis(fd, []);
  };

  // `files` is the drop being read; empty for a read of the server's stored copies.
  const runAnalysis = async (fd, files) => {
    if (running.current) {
      // A second click on "Read now" is dropped quietly; a new drop is told to wait.
      if (files.length) toast({ title: 'Audrey is still reading', description: 'Drop the files again when she is done.' });
      return;
    }
    running.current = true;
    setAnalyzing(true);
    fd.append('loan_type', loanType);
    try {
      const { data } = await api.post('/income/auto-analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const rows = data.files || [];
      if (files.length === 0 && rows.length > 0 && rows.every((f) => f.error)) {
        // No stored copy could be read: say why and keep what is on screen.
        toast({ title: 'Could not read the file', description: rows[0].error, variant: 'destructive' });
        return;
      }
      setAnalysis(data);
      // The drop on screen is now this one, so a review re-reads these files.
      lastFiles.current = files;
      // If a primary calculation was determined, sync the manual form for editing
      if (data.primary_income_input) {
        // Map all W-2 variants → unified 'w2_wage' tile
        const raw = data.primary_income_input.income_type || 'w2_salary';
        const uiType = ['w2_salary', 'w2_hourly', 'w2_overtime_bonus'].includes(raw) ? 'w2_wage' : raw;
        // Merge OT figures from aggregate into the form if variable pay was detected
        const merged = { ...data.primary_income_input };
        const varYtd = data.aggregate_pay?.variable_ytd || 0;
        if (varYtd > 0) {
          merged.overtime_y2 = varYtd; // treat aggregate variable YTD as year-2 variable amount
        }
        if (uiType !== typeRef.current) {
          // The type-change effect applies the fill after its reset, or a
          // non-W-2 primary (e.g. a refused Sch C) never reaches the result card.
          pendingFill.current = { form: merged, result: data.primary_calculation || null };
          setType(uiType);
        } else {
          setForm(merged);
          if (data.primary_calculation) setResult(data.primary_calculation);
        }
      } else if (data.primary_calculation) setResult(data.primary_calculation);
      const incomeCount = data.files.filter(f => f.is_income_document).length;
      const heldCount = data.files.filter(f => f.needs_masking).length;
      const skippedCount = data.files.length - incomeCount - heldCount;
      // The PTO rule is a paystub rule: say it only when a paystub was read (ticket 216).
      const readPaystub = data.files.some(f => f.is_income_document && f.classified_as === 'paystub');
      toast({
        title: `${incomeCount} document${incomeCount !== 1 ? 's' : ''} analyzed`,
        description: heldCount > 0
          ? `${heldCount} file${heldCount !== 1 ? 's need' : ' needs'} an SSN mask review before Audrey can read ${heldCount !== 1 ? 'them' : 'it'}.`
          : skippedCount > 0 ? `${skippedCount} non-income file${skippedCount !== 1 ? 's' : ''} skipped.`
            : readPaystub ? 'PTO & holiday counted as base income.' : undefined,
      });
      loadHist();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Analysis failed', description: e.response?.data?.detail || 'Please try clearer scans.' });
    } finally {
      running.current = false;
      setAnalyzing(false);
      setDropReviewed(new Set());
      loadWaiting();
    }
  };

  // After a review from the waiting list: once none of my files still needs a
  // review, Audrey reads all my reviewed ones together (two years of returns
  // pair up). Until then they wait with a "Read now" button each.
  const afterWaitingReview = async () => {
    const mine = (await loadWaiting()).filter((i) => i.mine);
    const ready = mine.filter((i) => i.status === 'reviewed');
    if (ready.length && !mine.some((i) => i.status === 'held')) await readReviewed(ready.map((i) => i.review_id));
  };

  // After an SSN mask review of a file in this drop: once the drop's last held
  // file is reviewed, run the same drop again (the review is keyed to the file's
  // bytes, so the held files now pass the gate). Until then the reviewed ones
  // wait. With the drop gone from the page, the server's copy is read.
  const reanalyze = (out) => {
    if (!lastFiles.current.length) return afterWaitingReview();
    const done = new Set(dropReviewed);
    if (out?.review_id) done.add(out.review_id);
    const left = (analysis?.files || []).filter((f) => f.needs_masking && !done.has(f.review_id));
    if (out?.review_id && left.length) { setDropReviewed(done); loadWaiting(); return undefined; }
    return analyzeDocs(lastFiles.current);
  };

  // The drop's files, with the ones reviewed and waiting for the others marked.
  const analysisShown = useMemo(() => {
    if (!analysis || dropReviewed.size === 0) return analysis;
    return { ...analysis, files: (analysis.files || []).map((f) => (dropReviewed.has(f.review_id) ? { ...f, ssn_reviewed: true } : f)) };
  }, [analysis, dropReviewed]);

  // A file with its Review button in the current drop's result is not listed
  // twice. A reviewed one is always listed, so "Read now" is within reach even
  // when the drop's other held files cannot be reviewed (expired, unreadable).
  const waitingElsewhere = useMemo(() => {
    const shown = new Set((analysis?.files || []).filter((f) => f.needs_masking).map((f) => f.review_id).filter(Boolean));
    return waiting.filter((i) => !(shown.has(i.review_id) && i.status === 'held'));
  }, [waiting, analysis]);

  const onDrop = (e) => { e.preventDefault(); setDragging(false); analyzeDocs(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  return {
    type, setType, form, setForm, setNum, result, busy, calc,
    history, del, detailCalcId, setDetailCalcId,
    loanType, setLoanType, agencyRules, currentRules,
    dragging, analyzing, analysis: analysisShown, setAnalysis, requestingDocs, requestVariableIncomeDocs,
    sendToBorrower, setSendToBorrower, fileInputRef, analyzeDocs, reanalyze, onDrop, onDragOver, onDragLeave,
    waiting: waitingElsewhere, readReviewed, afterWaitingReview,
    loanCtx, saveToLoan, savingToLoan, savedToLoan,
  };
}
